import { createApi } from "@reduxjs/toolkit/query/react";
import type { IApiResponse } from "@/types";
import type { IAssignedEmployee } from "@/types/(company)/earnings.interface";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  ICreateDeductionRequest,
  IGetDeductionByIdRequest,
  IUpdateDeductionRequest,
  TGetDeductionsListingRequest,
  TGetDeductionsListingResponse,
  TSingleDeductionResponse,
} from "@/types/(company)/deductions.interface";

export const companyDeductionsApi = createApi({
  reducerPath: "companyDeductionsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["deductions"],
  endpoints: (builder) => ({
    createDeduction: builder.mutation<
      TSingleDeductionResponse,
      ICreateDeductionRequest
    >({
      query: (body) => ({
        url: "/company/settings/deductions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["deductions"],
    }),
    getDeductions: builder.query<
      TGetDeductionsListingResponse,
      TGetDeductionsListingRequest
    >({
      query: (params) => ({
        url: "/company/settings/deductions",
        method: "GET",
        params: params ?? undefined,
      }),
      providesTags: ["deductions"],
    }),
    /**
     * The employees currently paid through a component — the avatar stack's
     * dialog. Separate from the list, which carries only a three-person
     * preview, because a core component can be on every employee in the company.
     */
    getDeductionAssignedEmployees: builder.query<
      IApiResponse<IAssignedEmployee[]>,
      { id: number; page?: number; limit?: number; branchId?: number }
    >({
      query: ({ id, page, limit, branchId }) => ({
        url: `/company/settings/deductions/${id}/assigned-employees`,
        method: "GET",
        // `branchId` omitted rather than sent as undefined: the API reads an
        // absent value as "every branch", which is what the filter's All means.
        params: { page, limit, ...(branchId ? { branchId } : {}) },
      }),
      providesTags: ["deductions"],
    }),
    getDeductionById: builder.query<
      TSingleDeductionResponse,
      IGetDeductionByIdRequest
    >({
      query: ({ id }) => ({
        url: "/company/settings/deductions" + `/${id}`,
        method: "GET",
      }),
      providesTags: ["deductions"],
    }),
    updateDeduction: builder.mutation<
      TSingleDeductionResponse,
      IUpdateDeductionRequest
    >({
      query: ({ id, ...body }) => ({
        url: "/company/settings/deductions" + `/${id}`,
        method: "PATCH",
        body: body.data,
      }),
      invalidatesTags: ["deductions"],
    }),
    deleteDeduction: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: "/company/settings/deductions" + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["deductions"],
    }),
  }),
});

export const {
  useGetDeductionsQuery,
  useCreateDeductionMutation,
  useUpdateDeductionMutation,
  useDeleteDeductionMutation,
  useGetDeductionByIdQuery,
  useGetDeductionAssignedEmployeesQuery,
} = companyDeductionsApi;
